import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { useLearning } from '../../context/LearningContext';
import { scrollBehavior } from '../../utils/motion';
import SourcePane from './SourcePane';
import './DialogueSession.css';

const CHAR_LIMIT = 2000;
const CHAR_WARN = 1500;

const BLOOM_COLORS = {
  remember: '#00ff88',
  understand: '#00f5ff',
  apply: '#ff6b35',
  analyze: '#f59e0b',
  evaluate: '#ff3366',
  create: '#8b5cf6',
};

function BloomChip({ level, t }) {
  if (!level) return null;
  return (
    <span
      className="dialogue-bloom-chip"
      style={{ color: BLOOM_COLORS[level] || 'var(--accent)', borderColor: BLOOM_COLORS[level] || 'var(--accent)' }}
    >
      {t(`learn.bloomLevels.${level}`, level)}
    </span>
  );
}

function VerdictChip({ verdict, t }) {
  if (!verdict) return null;
  const map = {
    correct: { key: 'verdictCorrect', cls: 'verdict-correct' },
    partial: { key: 'verdictPartial', cls: 'verdict-partial' },
    incorrect: { key: 'verdictIncorrect', cls: 'verdict-incorrect' },
  };
  const v = map[verdict];
  if (!v) return null;
  return <span className={`dialogue-verdict-chip ${v.cls}`}>{t(`learn.dialogue.${v.key}`)}</span>;
}

export default function DialogueSession() {
  const { t } = useTranslation();
  const { id: dialogueId } = useParams();
  const navigate = useNavigate();
  const { respondToDialogue } = useLearning();

  const [history, setHistory] = useState([]);
  const [sessionState, setSessionState] = useState(null);
  const [summary, setSummary] = useState(null);
  const [modules, setModules] = useState([]);
  const [pathName, setPathName] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('etch.dialogue.sidebarCollapsed') === 'true'
  );
  const [sourcePaneCollapsed, setSourcePaneCollapsed] = useState(
    () => localStorage.getItem('etch.dialogue.sourceCollapsed') === 'true'
  );

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    localStorage.setItem('etch.dialogue.sidebarCollapsed', String(next));
  };

  const toggleSource = () => {
    const next = !sourcePaneCollapsed;
    setSourcePaneCollapsed(next);
    localStorage.setItem('etch.dialogue.sourceCollapsed', String(next));
  };

  useEffect(() => {
    fetch(`/api/learning/dialogue/${dialogueId}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        setHistory(data.history || []);
        setSessionState(data.sessionState);
        setModules(data.modules || []);
        setPathName(data.pathName || '');
        if (data.summary) setSummary(data.summary);
      })
      .catch(status => {
        if (status === 404) setSessionState({ expired: true });
        else setError(t('learn.dialogue.errorGeneric'));
      })
      .finally(() => setLoading(false));
  }, [dialogueId, t]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: scrollBehavior() });
  }, [history]);

  useEffect(() => {
    if (!submitting && !sessionState?.done) {
      inputRef.current?.focus();
    }
  }, [submitting, sessionState?.done]);

  const handleSubmit = useCallback(async () => {
    if (!inputValue.trim() || submitting) return;
    const answer = inputValue.trim();
    setInputValue('');
    setSubmitting(true);
    setError(null);

    setHistory(h => [...h, { role: 'user', text: answer }]);

    try {
      const data = await respondToDialogue(dialogueId, answer);
      const { evaluation, sessionState: newState, summary: newSummary } = data;

      const tutorText = evaluation.cardExhausted
        ? evaluation.feedback
        : `${evaluation.feedback}${evaluation.followUpQuestion ? '\n\n' + evaluation.followUpQuestion : ''}`;

      setHistory(h => [...h, {
        role: 'tutor',
        text: tutorText,
        verdict: evaluation.verdict,
        bloomLevel: evaluation.followUpBloomLevel,
      }]);
      setSessionState(newState);
      if (newSummary) setSummary(newSummary);
    } catch (err) {
      setHistory(h => [...h, { role: 'tutor', text: t('learn.dialogue.errorGeneric') }]);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }, [inputValue, submitting, dialogueId, respondToDialogue]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────

  if (loading) return <div className="review-loading">{t('learn.dialogue.loading')}</div>;

  if (error && !sessionState) {
    return (
      <div className="review-complete">
        <div className="transition-icon">⚠️</div>
        <h2>{t('learn.dialogue.errorTitle')}</h2>
        <p style={{ color: 'var(--text-secondary)' }}>{error}</p>
        <button className="btn-primary" onClick={() => navigate('/learn')}>{t('learn.session.backToDashboard')}</button>
      </div>
    );
  }

  // ── Expired session ──────────────────────────────────────────────────────

  if (sessionState?.expired) {
    return (
      <div className="review-complete">
        <div className="transition-icon">⏱️</div>
        <h2>{t('learn.dialogue.expiredTitle')}</h2>
        <p style={{ color: 'var(--text-secondary)' }}>{t('learn.dialogue.expiredBody')}</p>
        <button className="btn-primary" onClick={() => navigate('/learn')}>{t('learn.dialogue.startNew')}</button>
      </div>
    );
  }

  // ── Complete — non-graded topic summary ──────────────────────────────────
  // Art. 50 Abs. 1 / Annex III §3(b): the LLM's per-answer "score" (see
  // src/learning/dialogue-engine.js) stays server-side input for Bloom-level
  // progression only. It is never rendered as a percentage or "mastery"
  // grade here — only the Bloom level reached per topic, same framing as
  // Mastery Missions (missions.aiDisclosure: "not a grade or assessment").

  if (sessionState?.done && summary) {
    return (
      <div className="dialogue-complete">
        <div className="transition-icon">🎓</div>
        <h2>{t('learn.dialogue.completeTitle')}</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{t('learn.dialogue.summaryHint')}</p>

        {summary.cardScores?.length > 0 && (
          <div className="dialogue-card-table">
            <div className="dialogue-card-table-header">
              <span>{t('learn.dialogue.topicHeader')}</span>
              <span>{t('learn.dialogue.bloomReachedHeader')}</span>
            </div>
            {summary.cardScores.map((cs, i) => (
              <div key={i} className="dialogue-card-table-row">
                <span className="dialogue-card-front">{cs.cardFront}</span>
                <BloomChip level={cs.bloomReached} t={t} />
              </div>
            ))}
          </div>
        )}

        <button className="btn-primary" onClick={() => navigate('/learn')}>
          {t('learn.session.backToDashboard')}
        </button>
      </div>
    );
  }

  // ── Active workspace ──────────────────────────────────────────────────────

  const totalCards = sessionState?.totalCards || 1;
  const cardIndex = sessionState?.cardIndex || 0;
  const progressPct = Math.min(100, (cardIndex / totalCards) * 100);
  const charCount = inputValue.length;

  return (
    <div className="dialogue-workspace">
      {/* Workspace header */}
      <div className="workspace-header">
        <button
          className="workspace-collapse-btn"
          onClick={toggleSidebar}
          title={sidebarCollapsed ? t('learn.dialogue.showModules') : t('learn.dialogue.hideModules')}
        >
          {sidebarCollapsed ? '▶' : '◀'}
        </button>

        <div className="workspace-header-center">
          <div className="lesson-phase-label">
            <span className="phase-chip learn-chip">{t('learn.dialogue.phaseLabel')}</span>
            {pathName && <span className="workspace-path-name">{pathName}</span>}
            <span className="review-progress">
              {t('learn.dialogue.topicProgress', { current: Math.min(cardIndex + 1, totalCards), total: totalCards })}
            </span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        <button
          className="workspace-collapse-btn"
          onClick={toggleSource}
          title={sourcePaneCollapsed ? t('learn.dialogue.showSource') : t('learn.dialogue.hideSource')}
        >
          {sourcePaneCollapsed ? '📄' : `✕ ${t('learn.dialogue.sourceLabel')}`}
        </button>
      </div>

      {/* Art. 50 Abs. 1 AI Act: unconditional, machine- and screen-reader-
          readable disclosure that the counterpart is an AI whose feedback
          may be wrong and is a learning aid, not a grade. Same shape as the
          chat composer's and Mastery Missions' disclosure. */}
      <p className="dialogue-ai-disclosure" role="note">
        <Info size={13} aria-hidden="true" />
        <span>{t('learn.dialogue.aiDisclosure')}</span>
      </p>

      {/* Three-zone body */}
      <div className="workspace-body">
        {/* Module sidebar */}
        <div className={`workspace-sidebar${sidebarCollapsed ? ' collapsed' : ''}`}>
          {modules.length > 0 && (
            <>
              <div className="sidebar-title">{t('learn.dialogue.modulesLabel')}</div>
              {modules.map((mod, i) => (
                <div
                  key={mod.id}
                  className={`sidebar-module${mod.deck_id === sessionState?.currentDeckId ? ' active' : ''}`}
                >
                  <span className="sidebar-module-idx">{i + 1}</span>
                  <span className="sidebar-module-title">{mod.title}</span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Source pane */}
        <SourcePane
          dialogueId={dialogueId}
          cardIndex={sessionState?.cardIndex}
          collapsed={sourcePaneCollapsed}
        />

        {/* Dialogue chat */}
        <div className="workspace-dialogue">
          <div className="dialogue-history">
            {history.map((msg, i) => (
              <div key={i} className={`dialogue-message ${msg.role}`}>
                {msg.role === 'tutor' ? (
                  <div className="dialogue-bubble tutor-bubble" data-ai-generated="true">
                    <div className="dialogue-bubble-meta">
                      <span className="dialogue-role-label">{t('learn.dialogue.tutorLabel')}</span>
                      <span className="dialogue-ai-badge" title={t('learn.dialogue.aiBadgeLabel')}>
                        <span aria-hidden="true">{t('learn.aiBadge')}</span>
                        <span className="visually-hidden">{t('learn.dialogue.aiBadgeLabel')}</span>
                      </span>
                      {msg.bloomLevel && <BloomChip level={msg.bloomLevel} t={t} />}
                      {msg.verdict && <VerdictChip verdict={msg.verdict} t={t} />}
                    </div>
                    <div className="dialogue-bubble-text">
                      {msg.text.split('\n').map((line, j) =>
                        line.trim() ? <p key={j}>{line}</p> : <br key={j} />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="dialogue-bubble user-bubble">
                    <div className="dialogue-bubble-meta">
                      <span className="dialogue-role-label">{t('learn.dialogue.youLabel')}</span>
                    </div>
                    <div className="dialogue-bubble-text">
                      <p>{msg.text}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {submitting && (
              <div className="dialogue-message tutor">
                <div className="dialogue-bubble tutor-bubble">
                  <div className="dialogue-thinking">{t('learn.dialogue.thinking')}</div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="dialogue-input-area">
            {error && <p className="dialogue-error">{error}</p>}
            <div className="dialogue-input-row">
              <textarea
                ref={inputRef}
                className="dialogue-textarea"
                placeholder={t('learn.dialogue.inputPlaceholder')}
                value={inputValue}
                onChange={e => setInputValue(e.target.value.slice(0, CHAR_LIMIT))}
                onKeyDown={handleKeyDown}
                disabled={submitting}
                rows={3}
              />
              <button
                className="dialogue-send-btn"
                onClick={handleSubmit}
                disabled={!inputValue.trim() || submitting}
              >
                {t('learn.dialogue.send')}
              </button>
            </div>
            {charCount >= CHAR_WARN && (
              <div className="dialogue-char-count" style={{ color: charCount >= CHAR_LIMIT ? 'var(--error)' : 'var(--text-tertiary)' }}>
                {charCount} / {CHAR_LIMIT}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
